import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Paperclip, MagnifyingGlass, ArrowsClockwise, EnvelopeSimple, FileArrowDown } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  ricevuta:        { label: "Ricevuta",       cls: "bg-blue-100 text-blue-700 border-blue-200" },
  in_valutazione:  { label: "In valutazione", cls: "bg-yellow-100 text-yellow-700 border-yellow-300" },
  accettata:       { label: "Accettata",      cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rilasciata:      { label: "Rilasciata",     cls: "bg-purple-100 text-purple-700 border-purple-200" },
  respinta:        { label: "Respinta",       cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}

function fmtSize(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024*1024) return `${(n/1024).toFixed(0)} KB`;
  return `${(n/1024/1024).toFixed(2)} MB`;
}

export default function AdminProposalsPage() {
  const [data, setData] = useState({ items: [], total: 0, status_counts: {} });
  const [filters, setFilters] = useState({ status: "all", q: "", page: 1 });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [editStatus, setEditStatus] = useState("");
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: 20 };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.q) params.q = filters.q;
      const r = await axios.get(`${API}/admin/proposals`, { params, withCredentials: true });
      setData(r.data || { items: [], total: 0, status_counts: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (p) => {
    setActive(p);
    setEditStatus(p.status);
    setEditNote(p.admin_note || "");
  };

  const save = async () => {
    if (!active || saving) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/proposals/${active.id}`,
        { status: editStatus, admin_note: editNote }, { withCredentials: true });
      toast.success("Proposta aggiornata");
      setActive(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const downloadAttachment = (pid, idx, filename) => {
    window.open(`${API}/admin/proposals/${pid}/attachment/${idx}`, "_blank");
  };

  const replyEmail = (p) => {
    const subject = `Re: [Proposta] ${p.title}`;
    window.location.href = `mailto:${p.user_email}?subject=${encodeURIComponent(subject)}`;
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="admin-proposals-page">
      <PageHeader title="Proposte funzionalità" subtitle="Gestione delle proposte ricevute dagli utenti" />

      {/* Status counters */}
      <div className="grid grid-cols-5 gap-2">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <button key={k}
            onClick={() => setFilters(f => ({ ...f, status: f.status === k ? "all" : k, page: 1 }))}
            className={`p-3 rounded-lg border text-center transition ${m.cls} ${filters.status === k ? "ring-2 ring-offset-1 ring-[#1B4FD8]" : "opacity-80 hover:opacity-100"}`}
            data-testid={`counter-${k}`}>
            <div className="text-2xl font-bold">{data.status_counts?.[k] || 0}</div>
            <div className="text-xs uppercase tracking-wider mt-0.5">{m.label}</div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <Card className="p-3 border-[#0D1F3C]/8 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#0D1F3C]/40" />
          <Input value={filters.q} onChange={e => setFilters(f => ({...f, q: e.target.value, page: 1}))}
            placeholder="Cerca per titolo..." className="pl-8 h-9 w-64" data-testid="proposals-search" />
        </div>
        <Select value={filters.status} onValueChange={v => setFilters(f => ({...f, status: v, page: 1}))}>
          <SelectTrigger className="w-44 h-9" data-testid="proposals-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} className="gap-1 h-9 ml-auto" data-testid="proposals-refresh">
          <ArrowsClockwise size={14} /> Aggiorna
        </Button>
        <span className="text-xs text-[#0D1F3C]/50">{data.total} totali</span>
      </Card>

      <Card className="border-[#0D1F3C]/8 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-[#0D1F3C]/50">Caricamento…</div>
        ) : data.items.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#0D1F3C]/50" data-testid="proposals-empty">Nessuna proposta trovata</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0D1F3C]/5">
              <tr>
                <th className="text-left px-4 py-2 text-xs uppercase text-[#0D1F3C]/60">Data</th>
                <th className="text-left px-4 py-2 text-xs uppercase text-[#0D1F3C]/60">Workspace</th>
                <th className="text-left px-4 py-2 text-xs uppercase text-[#0D1F3C]/60">Utente</th>
                <th className="text-left px-4 py-2 text-xs uppercase text-[#0D1F3C]/60">Titolo</th>
                <th className="text-left px-4 py-2 text-xs uppercase text-[#0D1F3C]/60">Stato</th>
                <th className="text-left px-4 py-2 text-xs uppercase text-[#0D1F3C]/60">Allegati</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(p => {
                const m = STATUS_META[p.status] || STATUS_META.ricevuta;
                return (
                  <tr key={p.id} onClick={() => openDetail(p)} className="cursor-pointer hover:bg-[#1B4FD8]/5 border-t border-[#0D1F3C]/5" data-testid={`row-${p.id}`}>
                    <td className="px-4 py-3 text-xs font-mono text-[#0D1F3C]/60">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-[#0D1F3C]/70">{p.company_name || p.workspace_id?.slice(0, 8)}</td>
                    <td className="px-4 py-3 text-xs text-[#0D1F3C]/70">{p.user_email}</td>
                    <td className="px-4 py-3 font-medium text-[#0D1F3C]">{p.title}</td>
                    <td className="px-4 py-3"><Badge className={`${m.cls} text-xs border`}>{m.label}</Badge></td>
                    <td className="px-4 py-3 text-xs text-[#0D1F3C]/60 flex items-center gap-1">
                      {p.attachments?.length > 0 && (<><Paperclip size={12} /> {p.attachments.length}</>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* Detail drawer */}
      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="proposal-detail-drawer">
          {active && (
            <>
              <SheetHeader><SheetTitle className="text-lg">{active.title}</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-4">
                <div className="text-xs text-[#0D1F3C]/60 space-y-0.5">
                  <div><strong>Da:</strong> {active.user_name} &lt;{active.user_email}&gt;</div>
                  <div><strong>Workspace:</strong> {active.company_name || active.workspace_id}</div>
                  <div><strong>Inviata:</strong> {fmtDate(active.created_at)}</div>
                </div>
                <div className="bg-[#F4F7FB] p-4 rounded-md text-sm text-[#0D1F3C] whitespace-pre-wrap leading-relaxed">
                  {active.description}
                </div>
                {active.attachments?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-[#0D1F3C]/60 uppercase mb-2">Allegati ({active.attachments.length})</div>
                    <div className="space-y-1.5">
                      {active.attachments.map((a, i) => (
                        <button key={i} onClick={() => downloadAttachment(active.id, i, a.filename)}
                          className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-[#0D1F3C]/10 rounded text-sm hover:bg-[#1B4FD8]/5"
                          data-testid={`attachment-${i}`}>
                          <FileArrowDown size={14} className="text-[#1B4FD8]" />
                          <span className="flex-1 text-left truncate text-[#0D1F3C]">{a.filename}</span>
                          <span className="text-xs text-[#0D1F3C]/50 font-mono">{fmtSize(a.size)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="border-t border-[#0D1F3C]/10 pt-4 space-y-3">
                  <div>
                    <label className="text-xs uppercase text-[#0D1F3C]/60 mb-1 block">Stato</label>
                    <Select value={editStatus} onValueChange={setEditStatus}>
                      <SelectTrigger data-testid="detail-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase text-[#0D1F3C]/60 mb-1 block">Nota interna admin</label>
                    <Textarea value={editNote} onChange={e => setEditNote(e.target.value.slice(0, 5000))} rows={4}
                      placeholder="Nota visibile solo agli admin..." data-testid="detail-note" />
                    <p className="text-[10px] text-[#0D1F3C]/40 mt-1 text-right">{editNote.length}/5000</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => replyEmail(active)} className="gap-1.5" data-testid="detail-reply-email">
                      <EnvelopeSimple size={14} /> Rispondi via email
                    </Button>
                    <Button onClick={save} disabled={saving} className="bg-[#1B4FD8] hover:bg-[#0D1F3C] ml-auto" data-testid="detail-save">
                      {saving ? "Salvataggio…" : "Salva"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
